import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import AsciiCube from '../components/AsciiPortrait';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(formData.username, formData.password);

    if (!result.success) {
      setError(result.error || 'Login failed');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex">
      {/* Full width container with halftone and form side by side */}
      <div className="w-full flex">
        {/* Left - Halftone Portrait (takes more space) */}
        <div className="hidden lg:block lg:w-3/5 relative">
          <AsciiCube />
        </div>

        {/* Right - Text and Form */}
        <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 py-12">
          {/* Innovation Text Block */}
          <div className="mb-12 max-w-md">
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-2 tracking-tight">
              Innovation
            </h1>
            <h2 className="text-4xl lg:text-5xl font-light text-gray-400 mb-8 tracking-tight">
              as our core value.
            </h2>

            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              ikcore is at the core of everything we do. By developing cutting-edge
              infrastructure solutions, we aim to transform how developers access
              and utilize server power. From domain management to SSL automation,
              we're setting new standards for deployment platforms.
            </p>

            <p className="text-gray-500 text-sm leading-relaxed">
              At ikcore, innovation isn't just a goal—it's our driving force.
              We believe that the key to a sustainable future lies in bold ideas
              and forward-thinking solutions.
            </p>
          </div>

          {/* Login Form */}
          <div className="max-w-sm">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 rounded bg-red-500/10 border border-red-500/20 flex items-center gap-3">
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-red-400 text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="w-full px-4 py-3 bg-transparent border border-zinc-700 rounded
                           focus:border-white focus:outline-none
                           transition-colors text-white placeholder-zinc-500 text-sm"
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </div>

              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full px-4 py-3 pr-12 bg-transparent border border-zinc-700 rounded
                           focus:border-white focus:outline-none
                           transition-colors text-white placeholder-zinc-500 text-sm"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 rounded text-sm font-medium text-black bg-white
                         hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed 
                         transition-colors"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>

            <p className="text-zinc-600 text-xs mt-6">
              Need access?{' '}
              <a
                href="https://ikcore.cloud"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-white transition-colors"
              >
                Contact us
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;